import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// A string state that lives in the URL query, so a refresh (or a shared link) restores exactly where
// the user was. The key is removed when the value equals its default, keeping URLs clean. Changing
// one filter can also clear others (e.g. reset the page) in the same update. Uses `replace`, so it
// doesn't pile up browser-history entries.
export function useUrlParam(key: string, def = ''): [string, (value: string, alsoClear?: string[]) => void] {
    const [params, setParams] = useSearchParams();
    const value = params.get(key) ?? def;
    const set = useCallback((next: string, alsoClear: string[] = []) => {
        setParams(prev => {
            const n = new URLSearchParams(prev);
            if (next === '' || next === def) n.delete(key); else n.set(key, next);
            alsoClear.forEach(k => n.delete(k));
            return n;
        }, { replace: true });
    }, [key, def, setParams]);
    return [value, set];
}

// Sets several URL params in ONE update. React Router's setter doesn't queue back-to-back calls
// (each starts from the same snapshot), so anything that changes more than one key at once must
// go through this. A null/empty value removes the key.
export function useSetUrlParams(): (patch: Record<string, string | null>) => void {
    const [, setParams] = useSearchParams();
    return useCallback((patch: Record<string, string | null>) => {
        setParams(prev => {
            const n = new URLSearchParams(prev);
            Object.entries(patch).forEach(([k, v]) => { if (v === null || v === '') n.delete(k); else n.set(k, v); });
            return n;
        }, { replace: true });
    }, [setParams]);
}
